import { merge } from 'lodash-unified';
import currency from 'currency.js';
import { Store } from '@betgames/bg-state-manager';
import { ISingleBet } from '@bg-shared/business/Reports';
import { StoresEntity } from '@bg-shared/business/StoreFeature';
import { BetStatus, GameId } from '@bg-shared/enums';
import { CasinoAutoPlayEndReason } from '../enums';
import { ICasinoAutoPlay, ICasinoAutoPlayForm } from '../interfaces';

const initialAutoPlay: ICasinoAutoPlay = {
    isActive: false,
    currentRound: 0,
    totalWin: 0,
    lastWin: 0,
    autoPlayEndReason: CasinoAutoPlayEndReason.DEFAULT,
    lastWinRunId: 0,
    betInfo: {},
    form: {
        numberOfRounds: 0,
    },
};

export class CasinoAutoPlayEntity extends Store<ICasinoAutoPlay> {
    constructor(gameId: GameId) {
        super({
            key: `CasinoAutoplay/${gameId}`,
            default: initialAutoPlay,
        });
    }

    public updateAutoPlay(value: Partial<ICasinoAutoPlay>): void {
        this.update((draft) => {
            merge(draft, value);
        });
    }

    public updateAutoPlayForm(value: Partial<ICasinoAutoPlayForm>): void {
        this.update((draft) => {
            merge(draft.form, value);
        });
    }

    public clearAutoPlay(): void {
        this.update(initialAutoPlay);
    }

    public repeatAutoPlay(): void {
        this.update((draft) => {
            draft.isActive = true;
            draft.currentRound = 0;
            draft.totalWin = 0;
            draft.lastWin = 0;
            draft.autoPlayEndReason = CasinoAutoPlayEndReason.DEFAULT;
            draft.lastWinRunId = 0;
            draft.betInfo = {};
        });
    }

    public addBetInfo(betInfo: Pick<ISingleBet, 'id' | 'amountBet' | 'runId'>[]): void {
        if (!this.value.isActive) {
            return;
        }

        // add bet id if it doesn't exist already
        const newBetInfo = betInfo.filter((bet) => !this.value.betInfo[bet.id]);

        if (!newBetInfo.length) {
            return;
        }

        this.update((draft) => {
            newBetInfo.forEach((bet) => {
                draft.betInfo[bet.id] = Number(bet.amountBet) || 0;
            });
        });
    }

    public updateAutoPlayResult(
        results: Pick<ISingleBet, 'id' | 'runId' | 'status' | 'amountWon'>[],
    ): void {
        if (!this.value.isActive) {
            return;
        }

        this.update((draft) => {
            results.forEach((result) => {
                if (result.status === BetStatus.ACTIVE) {
                    return;
                }

                const betAmount = draft.betInfo[result.id];

                if (!betAmount) {
                    return;
                }

                if (result.status !== BetStatus.RETURNED) {
                    const amountWon = Number(result.amountWon) || 0;

                    // sum all winnings and losing
                    draft.totalWin = currency(draft.totalWin)
                        .add(amountWon)
                        .subtract(betAmount).value;

                    if (draft.lastWinRunId !== result.runId) {
                        draft.lastWinRunId = result.runId;
                        draft.lastWin = 0;
                    }

                    // sum all last win for same runId
                    draft.lastWin = currency(draft.lastWin).add(amountWon).value;
                }

                delete draft.betInfo[result.id];
            });
        });
    }
}

export const casinoAutoPlayEntity = new StoresEntity(CasinoAutoPlayEntity);
